import { X, Upload, Sparkles, Loader2, AlertCircle, GripVertical } from 'lucide-react';
import { useRef, useState } from 'react';
import { useI18n } from '@/contexts/i18n-hook';

interface ImageUploadSectionProps {
  fotoParte1: string;
  fotoParte2: string;
  fotoParte3: string;
  fotoParte4: string;
  analisandoIA: boolean;
  setFotoParte1: (foto: string) => void;
  setFotoParte2: (foto: string) => void;
  setFotoParte3: (foto: string) => void;
  setFotoParte4: (foto: string) => void;
  setMostrarDialogIA: (mostrar: boolean) => void;
  handleUploadImagem: (file: File, indice: 1 | 2 | 3 | 4, onErro?: (msg: string | null) => void) => void;
  handlePasteImagem: (e: React.ClipboardEvent<HTMLDivElement> | ClipboardEvent, indice?: 1 | 2 | 3 | 4) => void;
  erroImagem?: string | null;
  onErroImagem?: (msg: string | null) => void;
}

export function ImageUploadSection({
  fotoParte1,
  fotoParte2,
  fotoParte3,
  fotoParte4,
  analisandoIA,
  setFotoParte1,
  setFotoParte2,
  setFotoParte3,
  setFotoParte4,
  setMostrarDialogIA,
  handleUploadImagem,
  handlePasteImagem,
  erroImagem,
  onErroImagem,
}: ImageUploadSectionProps) {
  const { t } = useI18n();
  const [draggingSlots, setDraggingSlots] = useState<Record<number, boolean>>({});
  const slotArrastando = useRef<(1 | 2 | 3 | 4) | null>(null);

  const setDragging = (indice: 1 | 2 | 3 | 4, value: boolean) => {
    setDraggingSlots((prev) => ({ ...prev, [indice]: value }));
  };

  const fotos: Record<1 | 2 | 3 | 4, string> = { 1: fotoParte1, 2: fotoParte2, 3: fotoParte3, 4: fotoParte4 };
  const setFotos: Record<1 | 2 | 3 | 4, (foto: string) => void> = { 1: setFotoParte1, 2: setFotoParte2, 3: setFotoParte3, 4: setFotoParte4 };

  const trocarImagens = (origem: 1 | 2 | 3 | 4, destino: 1 | 2 | 3 | 4) => {
    const fotoOrigem = fotos[origem];
    const fotoDestino = fotos[destino];
    setFotos[origem](fotoDestino);
    setFotos[destino](fotoOrigem);
  };

  const handleDropImagem = (
    e: React.DragEvent<HTMLDivElement>,
    indice: 1 | 2 | 3 | 4
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(indice, false);

    // Reordenação interna: trocar imagens entre slots
    const slotInterno = e.dataTransfer.getData('text/x-slot-interno');
    if (slotInterno) {
      const origem = Number(slotInterno) as 1 | 2 | 3 | 4;
      slotArrastando.current = null;
      if (origem !== indice) {
        trocarImagens(origem, indice);
      }
      return;
    }

    // Upload externo de arquivos
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) {
      onErroImagem?.(t('apreciacaoModais.editarParte.imagens.erroSemArquivo'));
      return;
    }

    const imagens = files.filter((file) => file.type?.startsWith('image/'));
    if (imagens.length === 0) {
      onErroImagem?.(t('apreciacaoModais.editarParte.imagens.erroArquivoInvalido'));
      return;
    }

    // Primeira imagem vai para o slot alvo
    handleUploadImagem(imagens[0], indice, onErroImagem);

    // Distribuir imagens restantes pelos slots vazios
    if (imagens.length > 1) {
      const todosSlots: (1 | 2 | 3 | 4)[] = [1, 2, 3, 4];
      const slotsDisponiveis = todosSlots.filter((s) => s !== indice && !fotos[s]);

      const restantes = imagens.slice(1);
      const paraProcessar = Math.min(restantes.length, slotsDisponiveis.length);
      for (let i = 0; i < paraProcessar; i++) {
        handleUploadImagem(restantes[i], slotsDisponiveis[i], onErroImagem);
      }
    }
  };

  // Componente reutilizável para cada slot de imagem
  const ImageSlot = ({
    indice,
    foto,
    setFoto
  }: {
    indice: 1 | 2 | 3 | 4;
    foto: string;
    setFoto: (foto: string) => void;
  }) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700">
          {t('apreciacaoModais.editarParte.imagens.slotRotulo', { indice })} {indice === 1 && <span className="text-red-500">*</span>}
        </label>
        {indice === 1 && foto && (
          <button
            type="button"
            onClick={() => setMostrarDialogIA(true)}
            disabled={analisandoIA}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('apreciacaoModais.editarParte.imagens.analisarIATitle')}
          >
            {analisandoIA ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('apreciacaoModais.editarParte.imagens.analisando')}
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4" />
                {t('apreciacaoModais.editarParte.imagens.analisarIA')}
              </>
            )}
          </button>
        )}
      </div>
      <div
        className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors min-h-[150px] flex items-center justify-center
          ${draggingSlots[indice]
            ? 'border-blue-500 bg-blue-50'
            : 'border-gray-300 focus-within:border-blue-500 focus-within:bg-blue-50'
          }`}
        tabIndex={0}
        onPaste={(e) => handlePasteImagem(e, indice)}
        onFocus={(e) => e.currentTarget.style.outline = 'none'}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!draggingSlots[indice]) setDragging(indice, true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const nextTarget = e.relatedTarget as Node | null;
          if (nextTarget && e.currentTarget.contains(nextTarget)) {
            return;
          }
          setDragging(indice, false);
        }}
        onDrop={(e) => handleDropImagem(e, indice)}
      >
        {foto ? (
          <div
            className="relative w-full cursor-grab active:cursor-grabbing"
            draggable
            onDragStart={(e) => {
              slotArrastando.current = indice;
              e.dataTransfer.effectAllowed = 'move';
              e.dataTransfer.setData('text/x-slot-interno', String(indice));
              if (e.currentTarget.querySelector('img')) {
                e.dataTransfer.setDragImage(e.currentTarget.querySelector('img')!, 50, 50);
              }
            }}
            onDragEnd={() => {
              slotArrastando.current = null;
            }}
          >
            <img src={foto} alt={t('apreciacaoModais.editarParte.imagens.slotAlt', { indice })} className="max-w-full h-auto rounded max-h-[200px] mx-auto" />
            <div className="absolute top-2 left-2 bg-black/50 text-white rounded-full p-1" title={t('apreciacaoModais.editarParte.imagens.arrastarReordenar')}>
              <GripVertical className="h-4 w-4" />
            </div>
            <button
              type="button"
              onClick={() => setFoto('')}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="w-full">
            <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <input
              type="file"
              accept="image/*,.heic,.heif"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleUploadImagem(e.target.files[0], indice, onErroImagem);
                  e.target.value = ''; // resetar para permitir re-upload do mesmo arquivo
                }
              }}
              className="hidden"
              id={`upload-parte-${indice}`}
              required={indice === 1}
            />
            <label
              htmlFor={`upload-parte-${indice}`}
              className="cursor-pointer text-sm text-blue-600 hover:text-blue-800 block"
            >
              {t('comum.arquivo.cliqueUpload')}
            </label>
            <p className="text-xs text-gray-500 mt-2">
              {t('apreciacaoModais.editarParte.imagens.dicaUpload')}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-4">
        {t('apreciacaoModais.editarParte.imagens.titulo')}
      </label>
      {erroImagem && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{erroImagem}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <ImageSlot indice={1} foto={fotoParte1} setFoto={setFotoParte1} />
        <ImageSlot indice={2} foto={fotoParte2} setFoto={setFotoParte2} />
        <ImageSlot indice={3} foto={fotoParte3} setFoto={setFotoParte3} />
        <ImageSlot indice={4} foto={fotoParte4} setFoto={setFotoParte4} />
      </div>
    </div>
  );
}
